<template>
    <div
        class="assignments"
        v-if="portfolio">

        <design-2021-worldmap-component
            v-if="portfolio.worldmap"
            :continents-in="continents"
            :countries-in="countries"
            @continent-tabbed="selectCategoryByContinent($event)"
            @country-tabbed="showAssignment($event)"
            :use-in="'show-assignments'">
        </design-2021-worldmap-component>

        <div
            class="row"
            v-else>
            <div
                class="col-12 col-md-4 col-lg-3 left-side"
                v-if="portfolio.categories.length">
                <b-nav
                    class="assignments-categories"
                    vertical
                    pills>
                    <b-nav-item
                        class="assignment-category"
                        v-for="(category, catIndex) in portfolio.categories"
                        :key="catIndex"
                        @click="selectCategory(category)"
                        active-class="active"
                        :active="selectedCategory.id === category.id">
                        {{ category.name }}
                        <design2021-apprentice-display-html-component
                            class="assignments-status"
                            :html-content="getAssignmentsCompletedStatus(category)">
                        </design2021-apprentice-display-html-component>
                    </b-nav-item>
                </b-nav>
            </div>

            <div class="col-12 col-md-8 col-lg-9 right-side">
                <div
                    class="category-assignments apprentice-simple-card"
                    v-if="selectedCategory && selectedCategoryAssignments.length">
                    <div
                        class="component-title"
                        v-if="selectedCategoryAssignments.length">
                        <h5 class="title">{{ selectedCategory.name }}</h5>
                        <div class="tools">
                            <b-badge
                                class="badge-rating"
                                v-if="hasRatingResult(selectedCategory)"
                                pill
                                variant="primary">
                                <i class="fas fa-fw fa-trophy"></i>{{ getResultValue(selectedCategory) }}
                            </b-badge>

                            <b-button
                                class="btn-medal btn-rate-category"
                                v-b-tooltip.hover
                                v-if="canRate(selectedCategory)"
                                v-b-modal="'category-rating-' + selectedCategory.id"
                                :title="$t('portfolio.assessment')"
                                variant="primary-icon"
                                data-toggle="tooltip"
                                data-placement="bottom">
                            </b-button>
                            <design-2021-portfolio-show-rating-modal-component
                                v-if="canRate(selectedCategory)"
                                :rating-in="selectedCategory.portfolio_rating"
                                :key-name="'category-rating-' + selectedCategory.id"
                                :children-in="selectedCategory.assignments"
                                :child-title="$t('portfolio.assignment_results')"
                                :title="'Categorie beoordeling: ' + selectedCategory.name"
                                @save-rating="savePortfolioRating($event, selectedCategory)">
                            </design-2021-portfolio-show-rating-modal-component>
                        </div>
                    </div>
                    <div class="assignments">
                        <design-2021-portfolio-show-assignments-card-component
                            :items="selectedCategoryAssignments"
                            :project-id="projectId">
                        </design-2021-portfolio-show-assignments-card-component>
                    </div>
                </div>

                <div
                    class="category-assignments apprentice-simple-card"
                    v-else>
                    <div class="not-found with-header empty-assignments-list">
                        <div class="icon">
                            <i class="fas fa-exclamation-circle"></i>
                        </div>
                        <div class="alert-notice">{{ $t('portfolio.there_are_no_assignments_ready_yet') }}</div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Design2021ApprenticeDisplayHtmlComponent from '../../../common/design-2021-apprentice-display-html-component.vue';
import Design2021WorldmapComponent from '../../../worldmap/design-2021-worldmap-component.vue';

export default {
    name: 'design-2021-portfolio-show-assignments-component',
    components: { Design2021WorldmapComponent, Design2021ApprenticeDisplayHtmlComponent },
    props: ['portfolioIn', 'projectId', 'categoryId'],
    data() {
        return {
            portfolio: this.portfolioIn,
            activeTab: 0,
            selectedCategory: {},
        };
    },
    computed: {
        selectedCategoryAssignments() {
            let categories = [];
            if (this.selectedCategory && this.portfolio) {
                for (const [catIndex, category] of this.portfolio.categories.entries()) {
                    if (category.id === this.selectedCategory.id) {
                        categories = category.assignments.map((assignment) => {
                            return {
                                category: category.name,
                                name: assignment.name,
                                id: assignment.id,
                                portfolio_rating: assignment.portfolio_rating,
                                progress_status: assignment.progress_status,
                                deadline: assignment.deadlines.length >= 1 ? assignment.deadlines[0].agenda_item : null,
                                assignment_tags: assignment.assignment_tags,
                            };
                        });
                    }
                }
            }
            return categories;
        },
        continents() {
            let continents = {};

            this.portfolio.categories.forEach((cat) => {
                let statusData = [
                    { status: 'open', count: 0, color: 'var(--open)' },
                    { status: 'submitted', count: 0, color: 'var(--submitted)' },
                    { status: 'closed', count: 0, color: 'var(--closed)' },
                    { status: 'reopened', count: 0, color: 'var(--reopened)' },
                ];

                cat.assignments.forEach((assignment) => {
                    statusData[0].count += assignment.progress_status['open'];
                    statusData[1].count += assignment.progress_status['submitted'];
                    statusData[2].count += assignment.progress_status['closed'];
                    statusData[3].count += assignment.progress_status['reopened'];
                });

                continents[cat.continent] = {
                    continent: cat.continent,
                    categoryName: cat.name,
                    color: cat.color ? cat.color : '#012033',
                    state: 'none',
                    statusData: statusData,
                };
            });

            return continents;
        },
        countries() {
            let countries = {};

            this.portfolio.categories.forEach((cat) => {
                cat.assignments.forEach((assignment) => {
                    countries[assignment.country] = {
                        country: assignment.country,
                        assignmentName: assignment.name,
                        state: 'none',
                        color: assignment.color ? assignment.color : '#012033',
                        statusColor: assignment?.statuses[0]?.color ?? 'var(--open)',
                    };
                });
            });

            return countries;
        },
    },

    methods: {
        updateTabIndex(tabIndex) {
            const name = 'portfolio-show-' + this.portfolioIn.id;
            this.activeTab = tabIndex;
            localStorage.setItem(name, tabIndex);
        },
        canRate(category) {
            return !this.$authUser.isStudent() && category.portfolio_rating && category.portfolio_rating.type;
        },
        hasRatingResult(category) {
            return category.portfolio_rating && category.portfolio_rating.results.length;
        },
        getResultValue(category) {
            if (!category.portfolio_rating || !category.portfolio_rating.results.length) {
                return null;
            }

            if (category.portfolio_rating.type === 1) {
                const id = category.portfolio_rating.results[0].portfolio_rating_option_id;
                for (const option of category.portfolio_rating.options) {
                    if (option.id === id) {
                        return option.value;
                    }
                }
            }

            if (category.portfolio_rating.type === 2) {
                return category.portfolio_rating.results[0].value;
            }

            return null;
        },
        savePortfolioRating(values, category) {
            if (!values.rating.value && !values.rating.option_id && !category.portfolio_rating.results.length) {
                return;
            }

            this.alert.saving();

            let data = values.rating;

            data.project_id = this.projectId;

            if (!category.portfolio_rating.results.length) {
                this.axios.post(this.routes.portfolioRatingResult(), data).then(
                    (response) => {
                        category.portfolio_rating.results = [response.data];
                        this.alert.success();
                        // this.fireClose(); todo
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    },
                );
            } else {
                this.axios.post(this.routes.portfolioRatingResultUpdate(), data).then(
                    (response) => {
                        if (response.data) {
                            category.portfolio_rating.results = [response.data];
                        } else {
                            category.portfolio_rating.results = [];
                        }
                        this.alert.success();
                        // if (!this.assignment.statuses || !this.assignment.statuses.length || this.assignment.statuses[0].status !== 1) {
                        //     this.fireClose();
                        // } else {
                        //     this.alert.success();
                        // } todo
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    },
                );
            }
        },

        selectCategory(category) {
            const name = 'portfolio-show-' + this.portfolioIn.id + '-selectedCategory';
            this.selectedCategory = category;
            localStorage.setItem(name, category.id);
        },

        getAssignmentsCompletedStatus(category) {
            let completedAssignments = 0;
            let totalAssignments = 0;

            let open = 0;
            let closed = 0;
            let reopened = 0;
            let submitted = 0;
            for (const assignment of category.assignments) {
                totalAssignments += assignment.progress_status.total;
                completedAssignments += assignment.progress_status.closed + assignment.progress_status.submitted;

                open += assignment.progress_status.open;
                closed += assignment.progress_status.closed;
                reopened += assignment.progress_status.reopened;
                submitted += assignment.progress_status.submitted;
            }

            let className = '';
            if (reopened > 0) {
                className = 'percentage bg-danger';
            } else if (submitted > 0) {
                className = 'percentage bg-warning';
            } else if (open > 0) {
                className = 'percentage bg-open';
            } else if (closed === totalAssignments) {
                className = 'percentage bg-success';
            }

            return `<div class="assignments-completed-status"><span class="text-label">Voltooid:</span><span class="${className}">${completedAssignments}/${totalAssignments}</span>`;
        },

        showAssignment(countryCode) {
            let selectedAssignment = null;
            this.portfolioIn.categories.forEach((cat) => {
                cat.assignments.forEach((assignment) => {
                    if (assignment.country === countryCode) {
                        selectedAssignment = assignment;
                    }
                });
            });
            window.location.href = this.routes.assignmentShow(selectedAssignment.id, this.projectId);
        },
        selectCategoryByContinent(continent) {
            this.selectCategory(this.portfolio.categories.find((cat) => cat.continent === continent));
        },
    },
    mounted() {
        const name = 'portfolio-show-' + this.portfolioIn.id;
        if (this.categoryId) {
            let count = 0;
            for (const category of this.portfolioIn.categories) {
                if (category.id === this.categoryId) {
                    this.activeTab = count;
                    localStorage.setItem(name, count);
                    break;
                }
                count++;
            }
        } else {
            if (localStorage.getItem(name)) {
                this.activeTab = parseInt(localStorage.getItem(name));
            }
        }

        const category = 'portfolio-show-' + this.portfolioIn.id + '-selectedCategory';
        if (localStorage.getItem(category)) {
            for (const [catIndex, portfolioCategory] of this.portfolio.categories.entries()) {
                if (portfolioCategory.id === parseInt(localStorage.getItem(category))) {
                    this.selectedCategory = this.portfolio.categories[catIndex];
                }
            }
        } else {
            this.selectedCategory = this.portfolioIn.categories[0];
        }
    },
};
</script>

<style scoped lang="scss">
.left-side {
    margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
}

.assignments ::v-deep {
    .component-title {
        margin-right: map-get($grid-gutter-spacing, 'fifteen');
        border-bottom: 3px dotted $border-color;
        padding-bottom: map-get($grid-gutter-spacing, 'ten');
    }

    .tools {
        display: flex;
        flex-direction: row;
        align-items: center;
    }

    .badge-rating {
        font-size: 1.125rem;
        padding-top: map-get($grid-gutter-spacing, 'eight');
        padding-bottom: map-get($grid-gutter-spacing, 'eight');
        height: 37px;

        i {
            margin-right: map-get($grid-gutter-spacing, 'fifteen');
        }
    }

    .btn-rate-category {
        font-size: 1.125rem;
        margin-left: map-get($grid-gutter-spacing, 'fifteen');
        height: 37px;

        &:before {
            margin-right: 0;
        }
    }

    .assignments-categories {
        .nav-item {
            &:not(:last-child) {
                padding-bottom: map-get($grid-gutter-spacing, 'fifteen');
                margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
                border-bottom: 3px dotted $border-color;
            }
        }

        .nav-link {
            color: $text-color;
            padding: map-get($grid-gutter-spacing, 'fifteen');
            background-color: transparent;
            border-radius: $border-radius-content;
            border: 1px solid var(--primary);

            &:hover,
            &.active {
                font-weight: $font-weight-bold;
                background-color: $white;
            }
        }
    }
}

.assignments ::v-deep {
    .assignments-in-progress {
        padding: 0;

        .component-title {
            display: none;
        }
    }
}

::v-deep .assignments-completed-status {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    font-weight: $font-weight-medium;
    font-size: 0.75rem;

    .text-label {
        color: $text-muted-color;
    }

    .percentage {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        color: $white;
        width: 30px;
        height: 30px;
        border-radius: 50%;
    }
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .left-side {
        margin-bottom: 0;
    }
}
</style>
